/* eslint-disable filenames/match-exported */
/**
 * Sentry SDK for tracking exceptions.
 *
 * @see https://docs.sentry.io/error-reporting/quickstart/?platform=browsernpm
 */
import * as Sentry from '@sentry/browser';
import { BrowserTracing } from '@sentry/tracing';
import { debugLogger } from '@ichef/fe-utils';

// eslint-disable-next-line no-unused-vars
const SENTRY_DSN_DEV = 'https://40cbf06d47d342cdbdc94b0ac2d4d57f@o48589.ingest.sentry.io/5558891';
const SENTRY_DSN_TEAM = 'https://0120f64fa3ff42e7a3bf11498279011e@o48589.ingest.sentry.io/5558887';
const SENTRY_DSN_PROD = 'https://65c8f93462a742b3a4eb8d83c54d1c1f@o48589.ingest.sentry.io/5558852';

// istanbul ignore next
const sentryDSN = (() => {
  if (__PROD__ || __RC__) {
    return SENTRY_DSN_PROD;
  }
  if (__TEAM__) {
    return SENTRY_DSN_TEAM;
  }
  if (__TEST__) {
    return 'MOCKED_TEST_DSN';
  }
  if (__DEV__) {
    // Uncomment to log errors to dev project.
    // return SENTRY_DSN_DEV;
  }
  return null;
})();

/**
 * For testing Sentry in local dev environment:
 *
 * const SHOULD_TRACK = true;
 * const SENTRY_DSN = SENTRY_DSN_DEV;
 */

const LOCAL_HOSTNAME = 'local-shop.ichefpos.com';

const { hostname } = window.location;
const notFromLocalhost = __TEST__ ? true : hostname !== LOCAL_HOSTNAME;

export function initSentry() {
  if (sentryDSN && notFromLocalhost) {
    Sentry.init({
      beforeSend(event) {
        const isInHouse401Error = event.breadcrumbs?.some(
          (crumb) =>
            crumb.category === 'fetch' &&
            crumb.data.url.match(/^\/api/) &&
            crumb.data.status_code === 401,
        );

        if (isInHouse401Error) {
          // ignore this error
          return null;
        }
        return event;
      },
      autoSessionTracking: true,
      dsn: sentryDSN,
      // __SENTRY_RELEASE_VERSION will be injected in webpack config.
      // eslint-disable-next-line no-underscore-dangle
      // eslint-disable-next-line no-undef
      release: __SENTRY_RELEASE_VERSION__,
      environment: hostname,
      ignoreErrors: [
        'ChunkLoadError',
        'instantSearchSDKJSBridgeClearHighlight',
        'PaymentAutofillConfig',
        'Blocked a frame with origin "https://shop.ichefpos.com"',
      ],
      ignoreUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
      ],
      integrations: [new BrowserTracing()],
    });
  }
}

/**
 * Capture exception to Sentry with optional tags and context.
 *
 * @param {Error} exception
 * @param {Object} options
 * @param {{}} options.tag
 * @param {{}} options.context
 */
export function logException(exception, { tag, context } = {}) {
  if (sentryDSN) {
    Sentry.withScope((scope) => {
      if (tag) {
        Object.entries(tag).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }
      if (context) {
        Object.entries(context).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }
      Sentry.captureException(exception);
    });
  }

  // Print exception logs in console under non-production
  debugLogger.group('[LogException]', exception.message);
  debugLogger.error(exception);
  debugLogger.log('Tag:', tag);
  debugLogger.log('Context:', context);
  debugLogger.groupEnd();
}

export default Sentry;
